import type { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import type { MouseEventArgs } from "../core/events.js";
import type { Point } from "../../common/geometry.js";
import type { MenuItemSpec } from "./MenuItem.js";
import { fitInWorkArea, MenuPopup } from "./MenuPopup.js";

export interface ShowMenuOptions {
  /** Called once the menu is dismissed, whether or not an item ran. */
  onDismiss?: () => void;
}

/**
 * Open `items` as a floating menu with its top-left corner at `at`, in desktop
 * coordinates. The menu is nudged back on-screen if it would overflow, and
 * flips above the point when there is no room below.
 */
export function showMenu(
  desktop: Desktop,
  items: MenuItemSpec[],
  at: Point,
  options: ShowMenuOptions = {},
): MenuPopup | null {
  if (items.length === 0) return null;
  const menu = new MenuPopup({ items, onDismiss: options.onDismiss });
  const size = menu.measureIn(desktop);
  const area = desktop.workArea;

  // Prefer down-and-right of the point, the way a native context menu opens.
  const x = at.x + size.width <= area.x + area.width ? at.x : at.x - size.width;
  const y = at.y + size.height <= area.y + area.height ? at.y : at.y - size.height;

  const corner = fitInWorkArea(desktop, { x, y }, size);
  menu.setBounds(corner.x, corner.y, size.width, size.height);
  desktop.showPopup(menu);
  menu.focus();
  return menu;
}

/**
 * Open a context menu for a right-click on `control`, at the pointer.
 *
 * ```ts
 * list.on("contextmenu", (e) => showContextMenu(list, e, [
 *   { text: "&Open", onClick: open },
 *   menuSeparator,
 *   { text: "&Delete", enabled: canDelete, onClick: remove },
 * ]));
 * ```
 */
export function showContextMenu(
  control: Control,
  e: MouseEventArgs,
  items: MenuItemSpec[],
  options: ShowMenuOptions = {},
): MenuPopup | null {
  const desktop = control.desktop;
  if (!desktop) return null;
  e.handled = true;
  return showMenu(desktop, items, e.screen, options);
}

/**
 * Attach `items` as `control`'s context menu, replacing any previous one.
 * Returns a function that detaches it again.
 *
 * `items` may be a factory so the menu can reflect the state at the moment of
 * the click — which entries are enabled, what is selected.
 */
export function attachContextMenu(
  control: Control,
  items: MenuItemSpec[] | (() => MenuItemSpec[]),
  options: ShowMenuOptions = {},
): () => void {
  const handler = (e: MouseEventArgs): void => {
    const resolved = typeof items === "function" ? items() : items;
    if (resolved.length > 0) showContextMenu(control, e, resolved, options);
  };
  control.on("contextmenu", handler);
  return () => control.off("contextmenu", handler);
}
